import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { getMerchantDetails } from "../../../services/merchant.service";
import { DetailsLayout } from "../../../components/akapBigas/detailsLayout";

const StyledDiv = styled.div`
  margin-top: 23px;
`;

const MerchantHeader = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  letter-spacing: -0.002px;
  margin-bottom: 9px;
`;

const MerchantDetails = styled.div`
  font-size: 14px;
  color: #000;
  font-weight: 400;
  letter-spacing: -0.257px;
  margin-bottom: 9px;
`;

const MerchantLabel = styled.div`
  font-size: 11px;
  color: #000;
  font-weight: 600;
  margin-bottom: 4px;
`;

const MerchantDetailValue = styled.div`
  font-size: 14px;
  color: #000;
  margin-bottom: 4px;
  letter-spacing: -0.257px;
`;

const BusinessPermitImage = styled.img`
  max-width: 100%;
  width: 110px;
  height: 146px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
`;

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 8px;
`;

const SearchIconOverlay = styled.div`
  background: white;
  position: absolute;
  bottom: 4px;
  right: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  .anticon {
    color: #666;
    font-size: 12px;
  }

  &:hover .anticon {
    color: white;
  }
`;

const FullSizeImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  text-align: center;
  padding: 16px;
`;

const ImageError = styled.div`
  color: #ff4d4f;
  font-size: 14px;
  margin: 20px 0;
`;

const DownloadButton = styled.button`
  margin-top: 16px;
  padding: 8px 16px;
  background-color: #52c41a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #389e0d;
  }

  &:disabled {
    background-color: #d9d9d9;
    cursor: not-allowed;
  }
`;

const NoImageMessage = styled.div`
  font-size: 14px;
  color: #666;
  font-style: italic;
  margin-top: 8px;
`;

const Divider = styled.hr`
  margin: 16px 0;
  border: none;
  height: 1px;
  background-color: #e0e0e0;
`;

const MerchantInfo = () => {
  const { id } = useParams();
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState("");
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [businessPermitImageError, setBusinessPermitImageError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMerchantDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await getMerchantDetails(id);
      setMerchantDetails(details);
      setBusinessPermitImageError(false); // Reset error state when new data is loaded
    } catch (err) {
      console.error("Error fetching merchant details:", err);
      setError(err.message || "Failed to load merchant details");
      setMerchantDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchMerchantDetails();
    }
  }, [id]);

  const handleImageClick = imageSrc => {
    if (imageSrc) {
      setCurrentImageSrc(imageSrc);
      setIsImageModalVisible(true);
      setImageError(false);
      setImageLoading(true);
    }
  };

  const handleCloseModal = () => {
    setIsImageModalVisible(false);
    setCurrentImageSrc("");
    setImageError(false);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleOpenInNewTab = () => {
    if (currentImageSrc) {
      window.open(currentImageSrc, "_blank");
    }
  };

  const handleDownload = () => {
    if (currentImageSrc) {
      const link = document.createElement("a");
      link.href = currentImageSrc;
      link.download = "business_permit.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBusinessPermitImageError = () => {
    setBusinessPermitImageError(true);
  };

  return (
    <DetailsLayout title="Business Info">
      <StyledDiv>
        {loading && <div>Loading merchant details...</div>}
        {error && <div style={{ color: "red" }}>Error: {error}</div>}
        {!loading && !error && merchantDetails && (
          <>
            <MerchantHeader>{merchantDetails?.name}</MerchantHeader>
            <MerchantDetails>{merchantDetails?.overrides?.address}</MerchantDetails>
            <Divider />
            <MerchantLabel>Name of Owner</MerchantLabel>
            <MerchantDetailValue>{merchantDetails?.overrides?.merchant_details?.focal_full_name}</MerchantDetailValue>
            <Divider />
            <MerchantLabel>Contact Number</MerchantLabel>
            <MerchantDetailValue>
              {merchantDetails?.overrides?.merchant_details?.focal_phone_number || "No contact number available"}
            </MerchantDetailValue>
            <Divider />
            <MerchantLabel>Tin Number</MerchantLabel>
            <MerchantDetailValue>
              {merchantDetails?.overrides?.merchant_details?.tin || "No TIN number available"}
            </MerchantDetailValue>
            <Divider />
            <MerchantLabel>Business Permit</MerchantLabel>
            {merchantDetails?.overrides?.merchant_details?.business_permit ? (
              businessPermitImageError ? (
                <NoImageMessage>Failed to load business permit image</NoImageMessage>
              ) : (
                <ImageContainer>
                  <BusinessPermitImage
                    src={merchantDetails.overrides.merchant_details.business_permit}
                    alt="Business Permit"
                    onError={handleBusinessPermitImageError}
                  />
                  <SearchIconOverlay
                    onClick={() => handleImageClick(merchantDetails.overrides.merchant_details.business_permit)}>
                    <SearchOutlined />
                  </SearchIconOverlay>
                </ImageContainer>
              )
            ) : (
              <NoImageMessage>No business permit uploaded</NoImageMessage>
            )}
          </>
        )}
      </StyledDiv>

      {isImageModalVisible && (
        <Modal
          title="Business Permit"
          visible={isImageModalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width="80%"
          centered
          destroyOnClose>
          <ModalContent>
            {imageLoading && <div>Loading image...</div>}
            {imageError && (
              <ImageError>
                Unable to load image. The file may be corrupted or not accessible.
                <br />
                <button
                  onClick={handleOpenInNewTab}
                  style={{
                    marginTop: "10px",
                    padding: "8px 16px",
                    backgroundColor: "#1890ff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}>
                  Open in New Tab
                </button>
              </ImageError>
            )}
            {!imageError && currentImageSrc && (
              <>
                <FullSizeImage
                  src={currentImageSrc}
                  alt="Business Permit Full View"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
                <DownloadButton onClick={handleDownload}>Download Image</DownloadButton>
              </>
            )}
          </ModalContent>
        </Modal>
      )}
    </DetailsLayout>
  );
};

export default MerchantInfo;
